import { createFeatureSelector, createSelector } from '@ngrx/store';
// import { HttpStatus } from '@core/classes/http-status.class';
import { HttpErrorResponse } from '@angular/common/http';

import * as Objects from '@core/objects';
import * as Access from '@app/models/access';
import { VersionFeedback, ShareVersionFeedback } from '@core/models/version-feedback';
import { VersionShare, UploadVersionShare, VersionShareType } from '@core/models/version-share.model';
import { VersionShareFeedbackMessage } from '@core/models/version-share-message.model';
// export interface ProjectLoaded {
//     id: number;
//     project: Request;
//     status: HttpStatus;
// }

export const UPLOAD_VERSION_SHARES_CONTEXT = {
    current_user: "userUVSContext",
    all_users: "allUsersUVSContext",
}

// in snake style for help to find
interface VersionsErrors {
    load_version: HttpErrorResponse;
    load_version_files: HttpErrorResponse;
    load_version_messages: HttpErrorResponse;
    load_version_comments: HttpErrorResponse;
    load_editor_access: HttpErrorResponse;
    load_version_feedbacks: HttpErrorResponse;
    load_version_shares: HttpErrorResponse;
    load_upload_version_shares: HttpErrorResponse;
    load_version_project: HttpErrorResponse;
    set_version_status: HttpErrorResponse;
    set_version_labels: HttpErrorResponse,
    load_related_versions: HttpErrorResponse,
    load_feedback_notes_counter: HttpErrorResponse,
    load_header: HttpErrorResponse,
    load_share_feedback: HttpErrorResponse
}

interface VersionsViewer {
    id: number;
    version: Objects.Version;
    files: Array<Objects.File>;
    feedback_notes_counter: Array<Objects.AnnotationsCounter>;
    messages: Array<Objects.Message>;
    comments: Array<Objects.VersionMessage> // for feedback and 
    // update for version_share_messages generated in store reducer by versionShares completed jan-2023 (maybe to core store)
    version_share_messages: Array<VersionShareFeedbackMessage>; 
    feedbacks: Array<VersionFeedback>;
    version_shares: Array<VersionShare>;
    upload_version_shares: Array<UploadVersionShare>;
    all_upload_version_shares: Array<UploadVersionShare>;
    uploaded_version_uploads: Objects.Version[]; // uploaded from shares
    project: Objects.Request;
    relateds: Objects.Version[];
    share_feedback: ShareVersionFeedback;
}

interface VersionsEditor {
    access: Access.AccessVersion;
}


// new idea to action calls status
export interface VersionsActionStatus {
    // Define status properties here
    loading: boolean;
    success: boolean;
    error: HttpErrorResponse;
    dispatcher?: string;
}


// this will add one VersionsActionStatus for each call  
interface VersionsActions {
    archive_feedback: VersionsActionStatus;
    archive_version_share: VersionsActionStatus;
    reset_version_share: VersionsActionStatus;
    send_version_files: VersionsActionStatus;
    send_self_feedback_request: VersionsActionStatus;
}

export interface Versions {
    editor:  VersionsEditor;
    view: VersionsViewer;
    errors: VersionsErrors;
    actions_status: VersionsActions;
}



export const initialState: Versions = {
    editor: {
        access: null,
    },
    view: {
        id: null,
        version: null,
        files: [],
        feedback_notes_counter: [],
        messages: null,
        comments: null,
        version_share_messages: [],
        feedbacks: null,
        version_shares: null,
        upload_version_shares: null,
        all_upload_version_shares: null,
        uploaded_version_uploads: [],
        project: null,
        relateds: [],
        share_feedback: null
    },
    errors: {
        load_version: null,
        load_version_files: null,
        load_feedback_notes_counter: null,
        load_version_messages: null,
        load_version_comments: null,
        load_editor_access: null,
        load_version_feedbacks: null,
        load_version_shares: null,
        load_upload_version_shares: null,
        load_version_project: null,
        set_version_status: null,
        set_version_labels: null,
        load_related_versions: null,
        load_header: null,
        load_share_feedback: null
    },
    actions_status: {
        archive_feedback: {
            loading: false,
            success: false,
            error: null
        },
        archive_version_share: {
            loading: false,
            success: false,
            error: null
        },
        reset_version_share: {
            loading: false,
            success: false,
            error: null
        },
        send_version_files: {
            loading: false,
            success: false,
            error: null
        },
        send_self_feedback_request:{
            loading: false,
            success: false,
            error: null
        },
    }
};

const versionsFeatureSelector = createFeatureSelector('versions');

// Selectors
export const viewSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view
);

export const versionSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.version
);

export const versionRelatedsSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.relateds
);

export const versionFilesSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.files
);

export const versionMessagesSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.messages
);

export const versionCommentsSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.comments
);

export const versionVersionShareMessagesSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => {
        if (!state || !state.view.version_share_messages || !state.view.version) {
            return []; //  default value when not ready
        }

        return state.view.version_share_messages.filter((vm: VersionShareFeedbackMessage) => vm.version.id.toString() === state.view.version.id.toString());
    
    }
);

export const versionShareFeedbackSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.share_feedback
);

export const versionFeedbacksSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.feedbacks
);

export const versionVersionSharesSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.version_shares
);

export const versionUploadVersionSharesByVersionSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => {
        if (!state || !state.view.upload_version_shares || state.view.version == null) {
            return null; //  default value when not ready
        }

        const items = state.view.upload_version_shares.filter((uvs: UploadVersionShare) => 
            uvs.origin.version.id.toString() === state.view.version.id.toString()
            );

        return items ? items[0] : null;
    
    }
);

export const versionClosedUploadVersionSharesByVersionSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => {
        if (!state || !state.view.all_upload_version_shares || state.view.version == null) {
            return null; //  default value when not ready
        }

        const allItems = state.view.all_upload_version_shares.filter((uvs: UploadVersionShare) => 
            uvs.origin.version.id.toString() === state.view.version.id.toString() && 
            uvs.upload.version != null
            );

        return allItems ? allItems : null;
    
    }
);
export const versionUploadedVersionUploadsSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.uploaded_version_uploads
);

export const versionFeedbackNotesCounterSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.feedback_notes_counter
);

export const versionProjectSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.view.project
);

export const editorAccessAllSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.editor.access
);

// action status selector
export const actionArchiveStatusSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.actions_status.archive_version_share
);

export const actionResetStatusSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.actions_status.reset_version_share
);

export const actionSendVersionFilesSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.actions_status.send_version_files
);

export const actionSelfShareFeedbackRequestSelector = createSelector(
    versionsFeatureSelector,
    (state: Versions) => state.actions_status.send_self_feedback_request
);

export const editorAccessSelector = (key: string) => 
    createSelector(
        versionsFeatureSelector,
        (state: Versions) => state.editor.access!=null && state.editor.access.hasOwnProperty(key) ? state.editor.access[key] : false
);

export const errorSelector = (key: string) => 
    createSelector(
        versionsFeatureSelector,
        (state: Versions) => state.errors.hasOwnProperty(key) ? state.errors[key] : null
    );